import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faMicrophone, faMicrophoneSlash, faPhoneSlash, faDesktop, faPhone, faVideo,
    faWindowMinimize, faWindowMaximize, faWindowRestore, faUserPlus, faUsers, faMessage,
    faWandMagicSparkles, faVolumeHigh, faCheck
} from '@fortawesome/free-solid-svg-icons';

import { useCall } from '../../context/CallContext';
import { formatDuration } from '../../utils/webrtc';
import { holdConversationRoom, onSocket } from '../../utils/socket';
import useCallClock from './useCallClock';
import CallStage from './CallStage';
import CallSidePanel from './CallSidePanel';
import CallChatPanel from './CallChatPanel';

/**
 * The call in progress, as a window inside the portal.
 *
 * Opens at 80% of the screen, centred. Drag it by the title bar, resize it
 * from any edge or corner. Maximise (or double-click the title bar) fills the
 * whole screen, and Restore puts it back where it was. Minimise sends it to
 * the pill in the top-right corner (CallWidget), and expanding from there
 * opens it at 80% again.
 *
 * Deliberately not a modal — no backdrop, nothing blocked outside its own
 * rectangle — so someone on a call can still look something up in the portal.
 *
 * The remote audio element lives in CallProvider, not here: minimising
 * unmounts this component, and the sound must carry on.
 */

const MIN_W = 360;
const MIN_H = 400;
const OPEN_RATIO = 0.8;
const HANDLES = ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'];

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const centredRect = () => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const w = Math.min(Math.max(Math.round(vw * OPEN_RATIO), MIN_W), vw);
    const h = Math.min(Math.max(Math.round(vh * OPEN_RATIO), MIN_H), vh);
    return { x: Math.round((vw - w) / 2), y: Math.round((vh - h) / 2), w, h };
};

/** Keep the whole window on screen — a title bar dragged off the edge cannot be grabbed back. */
const fitRect = (r) => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const w = Math.min(r.w, vw);
    const h = Math.min(r.h, vh);
    return { w, h, x: clamp(r.x, 0, vw - w), y: clamp(r.y, 0, vh - h) };
};

const resizeBy = (dir) => (r, dx, dy) => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let { x, y, w, h } = r;

    if (dir.includes('e')) w = clamp(r.w + dx, MIN_W, vw - r.x);
    if (dir.includes('s')) h = clamp(r.h + dy, MIN_H, vh - r.y);
    // The left and top edges move the origin, with the opposite edge pinned.
    if (dir.includes('w')) {
        w = clamp(r.w - dx, MIN_W, r.x + r.w);
        x = r.x + r.w - w;
    }
    if (dir.includes('n')) {
        h = clamp(r.h - dy, MIN_H, r.y + r.h);
        y = r.y + r.h - h;
    }
    return { x, y, w, h };
};

/*
 * Maximise also takes the browser full screen, so the call covers the monitor
 * rather than just the page. The whole document goes full screen, not the
 * window element: alerts are rendered outside the call window and would be
 * invisible behind an element-level full screen.
 */
const enterFullscreen = () => {
    const root = document.documentElement;
    if (document.fullscreenElement || !root.requestFullscreen) return;
    root.requestFullscreen().catch(() => { /* refused — the window still fills the page */ });
};

const exitFullscreen = () => {
    if (document.fullscreenElement && document.exitFullscreen) {
        document.exitFullscreen().catch(() => { });
    }
};

const ControlButton = ({ icon, label, onClick, variant = '', disabled = false, badge }) => (
    <div className="call-window-control">
        <button
            type="button"
            className={`call-window-btn ${variant}`.trim()}
            onClick={onClick}
            disabled={disabled}
            title={label}
            aria-label={label}
        >
            <FontAwesomeIcon icon={icon} />
            {badge && <span className="call-window-badge">{badge}</span>}
        </button>
        <span className="call-window-label">{label}</span>
    </div>
);

/**
 * Speaker / earpiece / headset — whichever output devices this browser
 * exposes (see utils/audioOutput.js for why the list can be empty: Safari
 * and iOS never support choosing one at all, and this control isn't rendered
 * there).
 */
const AudioOutputControl = ({ current, options, onSelect, disabled }) => {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);
    const menuRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const close = (e) => {
            if (menuRef.current?.contains(e.target)) return;
            if (wrapRef.current?.contains(e.target)) return;
            setOpen(false);
        };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, [open]);

    const currentLabel = options.find((o) => o.deviceId === current)?.label || 'Automatic';

    const choose = (deviceId) => {
        onSelect(deviceId);
        setOpen(false);
    };

    return (
        <div className="call-window-control" ref={wrapRef}>
            <button
                type="button"
                className={`call-window-btn ${open ? 'is-on' : ''}`.trim()}
                onClick={() => setOpen((o) => !o)}
                disabled={disabled}
                title={`Audio output: ${currentLabel}`}
                aria-label="Choose audio output"
                aria-haspopup="menu"
                aria-expanded={open}
            >
                <FontAwesomeIcon icon={faVolumeHigh} />
            </button>
            <span className="call-window-label">Audio</span>

            {open && (
                <div className="call-window-menu" ref={menuRef} role="menu">
                    <button
                        type="button"
                        role="menuitemradio"
                        aria-checked={!current}
                        className={`call-window-menu-item ${!current ? 'is-selected' : ''}`.trim()}
                        onClick={() => choose('')}
                    >
                        <span>Automatic</span>
                        {!current && <FontAwesomeIcon icon={faCheck} />}
                    </button>
                    {options.map((device) => (
                        <button
                            key={device.deviceId}
                            type="button"
                            role="menuitemradio"
                            aria-checked={current === device.deviceId}
                            className={`call-window-menu-item ${current === device.deviceId ? 'is-selected' : ''}`.trim()}
                            onClick={() => choose(device.deviceId)}
                        >
                            <span>{device.label}</span>
                            {current === device.deviceId && <FontAwesomeIcon icon={faCheck} />}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

const CallWindow = () => {
    const {
        call, me, micOn, toggleMic, hangUp, minimizeCall,
        headline, maxParticipants, sharingScreen, toggleScreenShare,
        noiseSuppressionOn, noiseSuppressionSupported, toggleNoiseSuppression,
        audioOutputId, audioOutputs, audioOutputSupported, setAudioOutput
    } = useCall();
    const seconds = useCallClock(call.answeredAt);

    const [rect, setRect] = useState(centredRect);
    const [gesture, setGesture] = useState(null);   // 'drag' | 'resize' | null
    const [panel, setPanel] = useState(null);       // 'people' | 'chat' | null
    const [maximized, setMaximized] = useState(false);
    const [unread, setUnread] = useState(0);

    const windowRef = useRef(null);
    const rectRef = useRef(rect);
    rectRef.current = rect;
    const endGestureRef = useRef(null);

    const panelOpen = panel !== null;
    const chatOpen = panel === 'chat';
    const chatId = call.chatConversationId;
    const togglePanel = (name) => setPanel((open) => (open === name ? null : name));

    const isVideo = call.kind === 'video';
    const onCall = call.participants.filter((p) => p.state === 'joined').length;
    const seatsLeft = Math.max(0, maxParticipants - call.participants.length);

    // Focused on open so Escape minimises straight away.
    useEffect(() => {
        windowRef.current?.focus({ preventScroll: true });
    }, []);

    // The browser window shrank — pull the call window back inside it.
    useEffect(() => {
        const onResize = () => setRect((r) => fitRect(r));
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    // The call can end mid-drag; don't leave listeners and a no-select body behind.
    useEffect(() => () => endGestureRef.current?.(), []);

    /*
     * Full screen follows the maximised state, both ways. Leaving it with the
     * browser's own Esc restores the window too — a maximised window sitting
     * in an ordinary tab is neither one thing nor the other.
     */
    useEffect(() => {
        if (!maximized) return undefined;
        enterFullscreen();
        const onChange = () => { if (!document.fullscreenElement) setMaximized(false); };
        document.addEventListener('fullscreenchange', onChange);
        return () => {
            document.removeEventListener('fullscreenchange', onChange);
            exitFullscreen();
        };
    }, [maximized]);

    /*
     * Count messages that arrive while the chat is closed, for the badge on
     * the Chat button. The room is held here as well as by the panel, so that
     * they arrive at all while it is shut.
     */
    useEffect(() => {
        if (!chatId) return undefined;
        const release = holdConversationRoom(chatId);
        const off = onSocket('message:new', (msg) => {
            if (String(msg.conversationId) !== String(chatId)) return;
            if (msg.systemEvent?.type) return;
            if (String(msg.sender?._id || msg.sender) === String(me)) return;
            setUnread((n) => n + 1);
        });
        return () => { off(); release(); };
    }, [chatId, me]);

    useEffect(() => { if (chatOpen) setUnread(0); }, [chatOpen, unread]);

    /**
     * Follow the pointer until it is released.
     *
     * Listeners go on window, not on the element: a fast drag easily outruns
     * the title bar, and the gesture must not stop the moment the pointer
     * leaves it.
     */
    const track = (kind, event, apply) => {
        if (event.button !== 0) return;
        event.preventDefault();

        const start = { x: event.clientX, y: event.clientY, rect: rectRef.current };
        setGesture(kind);
        document.body.classList.add('call-window-busy');

        const onMove = (ev) => setRect(apply(start.rect, ev.clientX - start.x, ev.clientY - start.y));
        const onUp = () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
            window.removeEventListener('pointercancel', onUp);
            document.body.classList.remove('call-window-busy');
            endGestureRef.current = null;
            setGesture(null);
        };

        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        window.addEventListener('pointercancel', onUp);
        endGestureRef.current = onUp;
    };

    const onBarPointerDown = (event) => {
        if (maximized || event.target.closest('button')) return;
        track('drag', event, (r, dx, dy) => fitRect({ ...r, x: r.x + dx, y: r.y + dy }));
    };

    // The desktop convention: double-clicking a title bar maximises or restores.
    const onBarDoubleClick = (event) => {
        if (event.target.closest('button')) return;
        setMaximized((m) => !m);
    };

    /*
     * One line for the whole call, not for one connection.
     *
     * Per-person state moved onto the tiles when the grid arrived, which is
     * where it belongs: with five connections coming up at different moments and
     * any one of them able to fail while the rest are fine, a single
     * "Connecting…" for the call as a whole would be a lie most of the time.
     * What is left here is the call's own state and its clock.
     */
    const statusLine = () => {
        if (call.status === 'outgoing') return 'Calling…';
        // Still in the call, waiting for the network to come back. The seat is
        // held for thirty seconds; see RECONNECT_GRACE_MS on the server.
        if (call.status === 'reconnecting') return 'Reconnecting…';
        if (call.status === 'connecting') return 'Connecting…';
        return formatDuration(seconds);
    };

    const classes = [
        'call-window',
        gesture && `is-${gesture}`,
        maximized && 'is-max',
        call.status === 'active' && 'is-live'
    ].filter(Boolean).join(' ');

    // Maximised fills the viewport outright; the remembered rect is what
    // Restore goes back to.
    const placement = maximized
        ? { left: 0, top: 0, width: '100%', height: '100%' }
        : { left: rect.x, top: rect.y, width: rect.w, height: rect.h };

    return (
        <div
            ref={windowRef}
            className={classes}
            style={placement}
            role="dialog"
            aria-label={`Call with ${headline}`}
            tabIndex={-1}
            /*
             * Escape closes the panel first, then restores a maximised window,
             * and minimises only when there is nothing else to undo. Minimising
             * out from under somebody who was part way through picking people
             * loses the selection with no way back.
             */
            onKeyDown={(event) => {
                if (event.key !== 'Escape') return;
                if (panelOpen) { setPanel(null); return; }
                if (maximized) { setMaximized(false); return; }
                minimizeCall();
            }}
        >
            <div
                className="call-window-bar"
                onPointerDown={onBarPointerDown}
                onDoubleClick={onBarDoubleClick}
            >
                <span className="call-window-title">
                    <FontAwesomeIcon icon={isVideo ? faVideo : faPhone} />
                    <span>{headline}</span>
                    {onCall > 2 && (
                        <span className="call-window-count">
                            <FontAwesomeIcon icon={faUsers} /> {onCall}
                        </span>
                    )}
                </span>
                <span className="call-window-clock">{statusLine()}</span>
                {/*
                    Adding people lives in the title bar rather than among the
                    call controls. Mute, share and hang up are things you do to
                    the call you are in; adding somebody changes who the call is
                    with, which is a different kind of act — and it opens a panel
                    rather than firing an action, so it belongs with the window's
                    own controls.
                */}
                <button
                    type="button"
                    className={panel === 'people' ? 'call-window-bar-btn is-on' : 'call-window-bar-btn'}
                    onClick={() => togglePanel('people')}
                    /*
                     * Not while it is still ringing out. There is no call id to
                     * invite into until the server has issued one, and the seat
                     * arithmetic would be guesswork.
                     */
                    disabled={call.status === 'outgoing'}
                    title={seatsLeft ? 'Add people' : 'This call is full'}
                    aria-label="Add people to the call"
                    aria-expanded={panel === 'people'}
                >
                    <FontAwesomeIcon icon={faUserPlus} />
                </button>
                <button
                    type="button"
                    className="call-window-bar-btn"
                    onClick={minimizeCall}
                    title="Minimise (Esc)"
                    aria-label="Minimise call"
                >
                    <FontAwesomeIcon icon={faWindowMinimize} />
                </button>
                <button
                    type="button"
                    className="call-window-bar-btn"
                    onClick={() => setMaximized((m) => !m)}
                    title={maximized ? 'Restore' : 'Maximise'}
                    aria-label={maximized ? 'Restore call window' : 'Maximise call window'}
                    aria-pressed={maximized}
                >
                    <FontAwesomeIcon icon={maximized ? faWindowRestore : faWindowMaximize} />
                </button>
            </div>

            <div className="call-window-body">
                {/*
                    The stage and the dock sit side by side; the controls run the
                    full width underneath, because they act on the call rather
                    than on either half of it.
                */}
                <div className="call-window-content">
                    <CallStage />
                    {panel === 'people' && <CallSidePanel onClose={() => setPanel(null)} />}
                    {chatOpen && <CallChatPanel onClose={() => setPanel(null)} />}
                </div>

                <div className="call-window-controls">
                    <ControlButton
                        icon={micOn ? faMicrophone : faMicrophoneSlash}
                        label={micOn ? 'Mute' : 'Unmute'}
                        variant={micOn ? '' : 'is-on'}
                        onClick={toggleMic}
                        disabled={call.status === 'outgoing'}
                    />
                    {noiseSuppressionSupported && (
                        <ControlButton
                            icon={faWandMagicSparkles}
                            label={noiseSuppressionOn ? 'Noise suppression on' : 'Noise suppression off'}
                            variant={noiseSuppressionOn ? 'is-on' : ''}
                            onClick={toggleNoiseSuppression}
                        />
                    )}
                    {audioOutputSupported && (
                        <AudioOutputControl
                            current={audioOutputId}
                            options={audioOutputs}
                            onSelect={setAudioOutput}
                            disabled={call.status === 'outgoing'}
                        />
                    )}
                    <ControlButton
                        icon={faDesktop}
                        label={sharingScreen ? 'Stop sharing' : 'Share screen'}
                        variant={sharingScreen ? 'is-on' : ''}
                        onClick={toggleScreenShare}
                        disabled={call.status === 'outgoing'}
                    />
                    <ControlButton
                        icon={faMessage}
                        label={chatOpen ? 'Hide chat' : 'Chat'}
                        variant={chatOpen ? 'is-on' : ''}
                        onClick={() => togglePanel('chat')}
                        disabled={call.status === 'outgoing'}
                        badge={!chatOpen && unread ? (unread > 9 ? '9+' : String(unread)) : null}
                    />
                    <ControlButton
                        icon={faPhoneSlash}
                        label={call.status === 'outgoing' ? 'Cancel' : 'End call'}
                        variant="is-danger"
                        onClick={hangUp}
                    />
                </div>
            </div>

            {!maximized && HANDLES.map((dir) => (
                <div
                    key={dir}
                    className={`call-window-handle is-${dir}`}
                    onPointerDown={(event) => track('resize', event, resizeBy(dir))}
                    aria-hidden="true"
                />
            ))}
        </div>
    );
};

export default CallWindow;
